import { ref, toRefs, reactive, computed, watch } from 'vue';
import { useRoute } from 'vue-router'
import { defineStore } from 'pinia'
import { useResponsive } from './useResponsive'

export const useAppLayout = defineStore('appLayout', () => {

  const dom = reactive({
    sidebar: null,
    toggleButton: null,
    logoHeader: null,
    htmlElement: null,
  })

  // ------------------------------------------------------------------------
  // prefs are User Preferences and are saved to localStorage
  // we use prefs to set the initial layout view on page load/reload
  // ------------------------------------------------------------------------
  const prefs = reactive({
    colorScheme: undefined,
    expandSidebar: undefined,
    // scale: 14,
  })

  // Shared, app-wide responsive breakpoints (see useResponsive / responsive-ui.md).
  // Exposed on layoutState so components only need one import (useAppLayout).
  const { isMobile, isTablet, isDesktop } = useResponsive()

  // ------------------------------------------------------------------------
  // layoutState is dynamic and changes with the current status of the layout.
  // note: it's possible for a user to have a preference (prefs) where the sidebar is collapsed
  //       by default on page load, but they can choose to expand it using a button, so layoutState will
  //       show the sidebar as expanded. However on page reload we defer to prefs to show as collapsed.
  // Responsive helpers (isMobile / isTablet / isDesktop) are included here so
  // consumers don't need a second useResponsive() import.
  // ------------------------------------------------------------------------
  const layoutState = reactive({
    isDark:    computed(() => layoutState.currentColorScheme == 'dark'),
    isLight:   computed(() => layoutState.currentColorScheme == 'light'),
    isMobile,   // ref auto-unwrapped → layoutState.isMobile is a plain boolean
    isTablet,
    isDesktop,
    showSidebar: undefined,
    showAdminDrawer: undefined,
    showReportsDrawer: undefined,
    showColorSchemeMenu: false,
    currentColorScheme: undefined,
    userPrefersDarkMode: undefined,
  })

  // ------------------------------------------------------------------------
  // NOTE:  The color scheme set in user prefs can be dark, light or automatic
  //        but the layoutState.currentColorScheme can only be dark or light, and is
  //        set below depending on prefs (saved to localStorage) and the user's
  //        browser settings.
  //        Thus, to properly change color scheme globally, simply set:
  //
  //        appLayout.prefs.colorScheme = <<'dark' or 'light' or 'automatic'>>
  // ------------------------------------------------------------------------
  watch(() => prefs.colorScheme, (val) => {
    // console.log('watch currentColorScheme', prefs.colorScheme)
    if (prefs.colorScheme=='dark' || (prefs.colorScheme=='automatic' && layoutState.userPrefersDarkMode)) {
      dom.htmlElement?.classList.add('dark-mode')
      layoutState.currentColorScheme = 'dark'
    } else {
      dom.htmlElement?.classList.remove('dark-mode')
      layoutState.currentColorScheme = 'light'
    }
  })

  watch(() => layoutState.showSidebar, (bool) => {
    closeAllSubMenus()
    // Only persist the sidebar preference at desktop (lg+) widths. Tablet portrait
    // forces a collapsed default and mobile uses the bottom nav, so neither should
    // overwrite the user's saved desktop choice.
    if (isDesktop.value) prefs.expandSidebar = bool
  })

  // Re-apply the responsive sidebar default whenever we cross a breakpoint.
  watch([isTablet, isDesktop], () => applySidebarDefault())

  function closeAllSubMenus() {
    Array.from(dom.sidebar?.getElementsByClassName('show') || []).forEach(ul => {
      ul.classList.remove('show')
      ul.previousElementSibling.classList.remove('rotate')
    })
  }

  const route = useRoute()
  const currentPath = ref(route.path)

  function initialize() {
    mountDomElements()
    // get user/browser setting for color scheme
    layoutState.userPrefersDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches
    // after elements are mounted, set dark mode if necessary
    if (prefs.colorScheme=='dark' || (prefs.colorScheme=='automatic' && layoutState.userPrefersDarkMode)) {
      dom.htmlElement.classList.add('dark-mode')
    }
    // Set the sidebar's expanded/collapsed default based on the current breakpoint.
    applySidebarDefault()
  }

  // Apply the responsive default for the sidebar's expanded/collapsed state:
  //   • mobile (< md):           handled by the bottom nav — leave layoutState untouched
  //   • tablet portrait (md–lg): default collapsed (60px icon rail)
  //   • desktop (lg+):           respect the user's saved preference
  function applySidebarDefault() {
    if (isMobile.value) return
    layoutState.showSidebar = isTablet.value ? false : (prefs.expandSidebar ?? true)
  }

  function mountDomElements() {
    dom.toggleButton  = document.getElementById('sidebar-toggle-btn')
    dom.sidebar       = document.getElementById('sidebar')
    dom.logoHeader    = document.getElementById('logo-header')
    dom.htmlElement   = document.documentElement
    // console.log("mountDomElements", dom)
  }

  function toggleSidebar() {
    layoutState.showSidebar = !layoutState.showSidebar
  }

  function toggleSubMenu(event){
    const button = event.target.closest('button')
    const subMenu = button.nextElementSibling

    // Close all submenus before opening this one, this creates an accordion affect
    if (!subMenu.classList.contains('show')) {
      closeAllSubMenus()
    }

    // open the current submenu, and rotate the caret button
    subMenu.classList.toggle('show')
    button.classList.toggle('rotate')

    // if the sidebar is collapsed when we try to open a submenu
    //   we expand the sidebar so we can see submenu items
    if (!layoutState.showSidebar) {
      layoutState.showSidebar = true
    }
  }

  // const isDesktop = computed(() => window.innerWidth > 991);

  function toggleAdminDrawer() {
    layoutState.showAdminDrawer = !layoutState.showAdminDrawer
  }

  function toggleReportsDrawer() {
    layoutState.showReportsDrawer = !layoutState.showReportsDrawer
  }

  return {
    initialize,
    toggleSidebar,
    toggleSubMenu,
    layoutState,
    prefs,
    currentPath,
    toggleAdminDrawer,
    toggleReportsDrawer,
  }
},
{
  persist: {pick: ['prefs']},
})
